// @flow
import * as ActionTypes from 'constants/ActionTypes';

const initialState = {
  serviceError: "",
  serviceCode: 200,
  reqStatus: "",
  isLoading: false,
  uploadResult: null,
  contacts: {
    list: [],
    total: 0,
    skip: 0,
    limit: 50,
  },
  contactDetail: null,
  contactLists: {
    lists: [],
  },
  subscriptions: {
    list: [],
    total: 0,
  },
  selectedContacts: [], // For multi-select
  notificationResult: null,
  messageTemplates: {
    templates: [],
    categories: {},
    total: 0,
  },
};

export default function(state: Object = initialState, action: Object) {
  switch (action.type) {
    /* Reset */
    case ActionTypes.SELLER_CONTACTS_RESET: {
      return {
        ...initialState,
      };
    }

    /* Upload Contacts */
    case ActionTypes.SELLER_UPLOAD_CONTACTS_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
        uploadResult: null,
      };
    }
    case ActionTypes.SELLER_UPLOAD_CONTACTS_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        uploadResult: action.payload,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_UPLOAD_CONTACTS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to upload contacts",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Get Contacts */
    case ActionTypes.SELLER_GET_CONTACTS_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_CONTACTS_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contacts: {
          list: action.payload.contacts || [],
          total: action.payload.total || 0,
          skip: action.payload.skip || 0,
          limit: action.payload.limit || 50,
        },
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_CONTACTS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to fetch contacts",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Get Contact Detail */
    case ActionTypes.SELLER_GET_CONTACT_DETAIL_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_CONTACT_DETAIL_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contactDetail: action.payload,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_CONTACT_DETAIL_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to fetch contact details",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Create Contact */
    case ActionTypes.SELLER_CREATE_CONTACT_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_CREATE_CONTACT_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contacts: {
          ...state.contacts,
          list: [action.payload, ...state.contacts.list],
          total: state.contacts.total + 1,
        },
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_CREATE_CONTACT_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to create contact",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Update Contact */
    case ActionTypes.SELLER_UPDATE_CONTACT_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_UPDATE_CONTACT_SUCCESS: {
      const updatedList = state.contacts.list.map(contact =>
        contact._id === action.payload._id ? action.payload : contact
      );
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contacts: {
          ...state.contacts,
          list: updatedList,
        },
        contactDetail: action.payload,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_UPDATE_CONTACT_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to update contact",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Delete Contact */
    case ActionTypes.SELLER_DELETE_CONTACT_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_DELETE_CONTACT_SUCCESS: {
      const filteredList = state.contacts.list.filter(
        contact => contact._id !== action.payload.contactId
      );
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contacts: {
          ...state.contacts,
          list: filteredList,
          total: state.contacts.total - 1,
        },
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_DELETE_CONTACT_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to delete contact",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Delete Multiple Contacts */
    case ActionTypes.SELLER_DELETE_MULTIPLE_CONTACTS_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_DELETE_MULTIPLE_CONTACTS_SUCCESS: {
      const filteredList = state.contacts.list.filter(
        contact => !action.payload.contactIds.includes(contact._id)
      );
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contacts: {
          ...state.contacts,
          list: filteredList,
          total: state.contacts.total - action.payload.contactIds.length,
        },
        selectedContacts: [],
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_DELETE_MULTIPLE_CONTACTS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to delete contacts",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Get Contact Lists */
    case ActionTypes.SELLER_GET_CONTACT_LISTS_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_CONTACT_LISTS_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contactLists: {
          lists: action.payload.lists || [],
        },
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_CONTACT_LISTS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to fetch contact lists",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Create Contact List */
    case ActionTypes.SELLER_CREATE_CONTACT_LIST_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_CREATE_CONTACT_LIST_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        contactLists: {
          lists: [action.payload, ...state.contactLists.lists],
        },
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_CREATE_CONTACT_LIST_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to create contact list",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Get Subscriptions */
    case ActionTypes.SELLER_GET_SUBSCRIPTIONS_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_SUBSCRIPTIONS_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        subscriptions: {
          list: action.payload.subscriptions || [],
          total: action.payload.total || 0,
        },
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_SUBSCRIPTIONS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to fetch subscriptions",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    /* Send Notification to Contacts */
    case ActionTypes.SELLER_SEND_NOTIFICATION_TO_CONTACTS_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
        notificationResult: null,
      };
    }
    case ActionTypes.SELLER_SEND_NOTIFICATION_TO_CONTACTS_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        notificationResult: action.payload,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_SEND_NOTIFICATION_TO_CONTACTS_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to send notifications",
        serviceCode: action.payload.statusCode || 500,
        notificationResult: null,
      };
    }

    /* Send Notification to List */
    case ActionTypes.SELLER_SEND_NOTIFICATION_TO_LIST_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
        notificationResult: null,
      };
    }
    case ActionTypes.SELLER_SEND_NOTIFICATION_TO_LIST_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        notificationResult: action.payload,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_SEND_NOTIFICATION_TO_LIST_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to send notifications to list",
        serviceCode: action.payload.statusCode || 500,
        notificationResult: null,
      };
    }

    /* Get Message Templates */
    case ActionTypes.SELLER_GET_MESSAGE_TEMPLATES_START: {
      return {
        ...state,
        isLoading: true,
        reqStatus: action.type,
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_MESSAGE_TEMPLATES_SUCCESS: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        messageTemplates: {
          templates: action.payload.templates || [],
          categories: action.payload.categories || {},
          total: action.payload.total || 0,
        },
        serviceError: "",
      };
    }
    case ActionTypes.SELLER_GET_MESSAGE_TEMPLATES_FAILURE: {
      return {
        ...state,
        isLoading: false,
        reqStatus: action.type,
        serviceError: action.payload.message || "Failed to fetch message templates",
        serviceCode: action.payload.statusCode || 500,
      };
    }

    default:
      return state;
  }
}

